import { AttributeSelectorType } from "@evergis/api";
import { useCallback, useMemo } from "react";
import { isEmpty, intersection } from "lodash";
import { createLayerConfiguration } from "../../../../evergis/core/layer/createLayerConfiguration";
import { LayerInfo } from "../../../../interfaces/resourceInfo";
import { ResourceMeta } from "../../../../hooks/useResourceMeta";
import isValidLayerReference from "../../DialogComponents/Attributes/LayerReferences/isValidLayerReference";
import { LayerReferenceItem } from "../../DialogComponents/Attributes/LayerReferences/types";
import { LayerAttribute } from "../components/Layer/LayerAttributeItem";
import { useLayerInfoWithUpdate } from "./useLayerInfoWithUpdate";

export interface useUpdateLayerParams {
  layer: LayerInfo;
  attributes: LayerAttribute[];
  displayField: string;
  meta?: ResourceMeta;
  layerReferences?: LayerReferenceItem[];
}

type LayerUpdate = [
  VoidFuncAsync,
  {
    layerUpdated: boolean;
    error?: Error;
    loading: boolean;
  },
];

// eslint-disable-next-line max-lines-per-function
export const useUpdateLayer = ({
  layer,
  attributes,
  displayField,
  meta,
  layerReferences,
}: useUpdateLayerParams): LayerUpdate => {
  const [{ layerUpdated, error, loading }, mutation] = useLayerInfoWithUpdate();

  // eslint-disable-next-line max-lines-per-function
  const updateLayer = useCallback(() => {
    const { alias, description, preview } = meta || {};
    const validLayerReferences = layerReferences ? layerReferences.filter(isValidLayerReference) : [];
    const layerReferencesIds = validLayerReferences.map(({ referenceId }) => referenceId);
    const allAttributes = [
      ...attributes.map(attribute => {
        if (!attribute.layerReferenceId) {
          return attribute;
        }

        if (isEmpty(intersection(layerReferencesIds, attribute.layerReferenceId))) {
          return { ...attribute, layerReferenceId: void 0, attributeSelectorType: AttributeSelectorType.None };
        }

        const filteredIds = attribute.layerReferenceId.filter(id => layerReferencesIds.includes(id));

        return {
          ...attribute,
          layerReferenceId: filteredIds,
          attributeSelectorType: isEmpty(filteredIds) ? AttributeSelectorType.None : attribute.attributeSelectorType,
        };
      }),
    ];

    const newConfiguration = {
      ...layer,
      configuration: {
        ...layer.configuration,
        attributesConfiguration: {
          ...layer.configuration.attributesConfiguration,
          layerReferences: validLayerReferences,
        },
      },
    };

    return mutation(
      {
        ...createLayerConfiguration(newConfiguration, allAttributes, displayField),
        alias,
        description,
      },
      preview,
    );
  }, [meta, mutation, layer, attributes, displayField, layerReferences]);

  return useMemo(() => [updateLayer, { layerUpdated, error, loading }], [updateLayer, loading, error, layerUpdated]);
};
